import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { apiService, PositionData, ApplySiteQuestion, hasInternalToken, clearInternalToken, getReferralRequireEmployeeId, getReferralRequireReferrerIdentity, getReferralIsCvRequired, getReferralIsEmailRequired, setReferralRequireEmployeeId, setReferralRequireReferrerIdentity, setReferralIsCvRequired, setReferralIsEmailRequired } from '../services/api';
import { getTranslations } from '../services/translations';
import { getPortalSession } from '../career-site/siteContext';
import { useFavicon } from '../hooks/useFavicon';
import { isCustomDomain, validateHostname, getCanonicalJobUrl } from '../utils/routing';
import { appendUtmParamsToFormData } from '../utils/utmTracking';
import { PhoneInput } from 'react-international-phone';
import { PhoneNumberUtil } from 'google-libphonenumber';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import 'react-international-phone/style.css';

interface PositionDetailsProps {
  isInternal?: boolean;
  isReferral?: boolean;
}

/** Where a gated position sends a visitor without a working token: the portal's gate inside a career site, else today's portal route. */
function portalGatePath(isReferral: boolean, portalCode: string): string {
  return getPortalSession()?.home ?? `/${isReferral ? 'referral' : 'internal'}/${portalCode}`;
}

const PositionDetailsContent: React.FC<PositionDetailsProps> = ({ isInternal = false, isReferral = false }) => {
  const isGated = isInternal || isReferral;
  const params = useParams<{ positionUuid: string; clientCode: string; clientUuid: string; apCode: string; second: string }>();
  // "second" comes from the 2-segment resolver routes (/:clientCode/:second or /:first/:second)
  const positionUuid = params.positionUuid || params.second;
  const { clientCode, clientUuid, apCode } = params;
  const isOnCustomDomain = isCustomDomain();
  // For internal/referral portals: clientCode in path mode, clientUuid in custom domain mode
  const portalCode = clientCode || clientUuid;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const trackingCode = searchParams.get('tracking_code');
  const [positionData, setPositionData] = useState<PositionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingTranslations] = useState(() => getTranslations(navigator.language || 'es-es'));
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(() => {
    // Referral portal: referrers can refer multiple candidates to the same
    // position, so do not gate on the local "already applied" cache. Duplicate
    // prevention for referrals is scoped to the referred candidate and must be
    // enforced on the backend.
    if (isReferral) {
      return false;
    }
    const appliedPositions = JSON.parse(localStorage.getItem('appliedPositions') || '[]');
    return appliedPositions.includes(positionUuid);
  });
  const [submittedThisSession, setSubmittedThisSession] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [isRecaptchaProcessing, setIsRecaptchaProcessing] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<File[]>([]);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showContactConsentModal, setShowContactConsentModal] = useState(false);
  const [showSmsConsentModal, setShowSmsConsentModal] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [multiChoiceSelections, setMultiChoiceSelections] = useState<Record<number, string[]>>({});
  const [otherText, setOtherText] = useState<Record<number, string>>({});
  const [otherSelected, setOtherSelected] = useState<Record<number, boolean>>({});
  const [singleChoiceSelections, setSingleChoiceSelections] = useState<Record<number, string>>({});
  const [needsSourceSelection, setNeedsSourceSelection] = useState(false);
  const [trackingPlatform, setTrackingPlatform] = useState('');
  // Under OTP verification the corporate email comes from the token, so no manual field is shown.
  const [internalRequiresVerification, setInternalRequiresVerification] = useState(false);
  const customJsScriptRef = useRef<HTMLScriptElement | null>(null);
  const applicationAsideRef = useRef<HTMLElement | null>(null);
  const [canStickApplicationForm, setCanStickApplicationForm] = useState(false);
  // Mirror these into refs so the 3s reCAPTCHA fallback (scheduled inside the
  // button's onClick) can read the latest values instead of the stale closure
  // captured at click time.
  const isSubmittingRef = useRef(isSubmitting);
  const isSubmittedRef = useRef(isSubmitted);
  useEffect(() => { isSubmittingRef.current = isSubmitting; }, [isSubmitting]);
  useEffect(() => { isSubmittedRef.current = isSubmitted; }, [isSubmitted]);
  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
  const infojobsApiKey = import.meta.env.VITE_INFOJOBS_APPLY_API_KEY;
  const infojobsWidgetSrc = import.meta.env.VITE_INFOJOBS_WIDGET_SRC;
  // Phone validation utility
  const phoneUtil = PhoneNumberUtil.getInstance();
  const isPhoneValid = (phone: string) => {
    try {
      return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    return () => { customJsScriptRef.current?.remove(); };
  }, []);

  useEffect(() => {
    const aside = applicationAsideRef.current;
    if (!positionData || !aside) return;

    const updateStickyState = () => {
      // A form taller than the viewport cannot remain sticky without hiding
      // its lower fields until the end of the job description.
      setCanStickApplicationForm(aside.offsetHeight + 64 <= window.innerHeight);
    };
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateStickyState);
    observer?.observe(aside);
    window.addEventListener('resize', updateStickyState);
    updateStickyState();
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateStickyState);
    };
  }, [positionData]);

  // Register tracking click (fire-and-forget)
  useEffect(() => {
    if (trackingCode) {
      apiService.registerTrackingClick(trackingCode).catch(() => {});
    }
  }, [trackingCode]);

  // Detect whether the tracking link requires the candidate to pick a source
  // (i.e. the link has no predefined platform configured).
  useEffect(() => {
    setTrackingPlatform('');
    if (!trackingCode) {
      setNeedsSourceSelection(false);
      return;
    }
    let cancelled = false;
    apiService
      .getTrackingLinkInfo(trackingCode)
      .then((info) => {
        if (!cancelled) setNeedsSourceSelection(!!info.needs_source_selection);
      })
      .catch(() => {
        if (!cancelled) setNeedsSourceSelection(false);
      });
    return () => {
      cancelled = true;
    };
  }, [trackingCode]);

  // Global callback function for reCAPTCHA - MUST be defined BEFORE loading the script
  useEffect(() => {
    // Define the submit callback function
    (window as any).onSubmit = async (token: string) => {
      // Prevent duplicate applications
      if (isSubmitted) {
        return;
      }
      // Custom validation for CV file - referral portal uses its own CV requirement setting
      const cvRequired = isReferral ? getReferralIsCvRequired() : positionData?.is_cv_required;
      if (positionData && cvRequired && !selectedFile) {
        setFormError(positionData.translations?.['apply.error_cv_required'] || 'Please select a CV file.');
        // Reset reCAPTCHA to allow retry
        if ((window as any).grecaptcha?.enterprise?.reset) {
          (window as any).grecaptcha.enterprise.reset();
        }
        setIsRecaptchaProcessing(false);
        return;
      }

      // Validate phone number
      if (!isPhoneValid(phoneNumber)) {
        setFormError(positionData?.translations?.['apply.error_invalid_phone'] || 'Please enter a valid phone number.');
        // Reset reCAPTCHA to allow retry
        if ((window as any).grecaptcha?.enterprise?.reset) {
          (window as any).grecaptcha.enterprise.reset();
        }
        setIsRecaptchaProcessing(false);
        return;
      }

      // When the tracking link has no predefined platform, the candidate must
      // pick a source from the client-configured list.
      if (needsSourceSelection && !trackingPlatform) {
        setFormError(positionData?.translations?.['apply.error_source_required'] || 'Please select a source.');
        if ((window as any).grecaptcha?.enterprise?.reset) {
          (window as any).grecaptcha.enterprise.reset();
        }
        setIsRecaptchaProcessing(false);
        return;
      }

      // Get the form element
      const formElement = document.getElementById('application-form') as HTMLFormElement;
      if (!formElement) {
        setFormError(positionData?.translations?.['apply.error_form'] || 'Form error. Please reload the page.');
        return;
      }

      // Validate form fields (HTML5 validation)
      if (!formElement.checkValidity()) {
        // Trigger HTML5 validation to show native error messages
        formElement.reportValidity();
        // Reset reCAPTCHA to allow retry
        if ((window as any).grecaptcha?.enterprise?.reset) {
          (window as any).grecaptcha.enterprise.reset();
        }
        setIsRecaptchaProcessing(false);
        return;
      }

      setIsSubmitting(true);
      setFormError(null);
      setIsRecaptchaProcessing(false);

      try {
        const formData = new FormData(formElement);

        // Ensure CV file is included when using drag & drop (only if file is selected)
        if (selectedFile) {
          formData.set('cv', selectedFile, selectedFile.name);
        }

        // Append additional documents if any
        if (selectedDocuments.length > 0) {
          selectedDocuments.forEach((doc) => formData.append('documents', doc, doc.name));
        }

        // Serialize killer questions for all question types
        const killerQuestions: Array<{ question: string; answer: string }> = [];
        const killerBlocks = formElement.querySelectorAll('[data-killer-idx]');
        killerBlocks.forEach((block, idx) => {
          const question = (block.querySelector(`input[name="killer_question_text_${idx}"]`) as HTMLInputElement)?.value;
          const radioInput = block.querySelector(`input[type="radio"][name="killer_${idx}"]:checked`) as HTMLInputElement;
          const textareaInput = block.querySelector(`textarea[name="killer_${idx}"]`) as HTMLTextAreaElement;
          const checkboxInputs = block.querySelectorAll(`input[type="checkbox"][name="killer_${idx}"]:checked`) as NodeListOf<HTMLInputElement>;
          const singleInput = block.querySelector(`select[name="killer_${idx}"], input[type="date"][name="killer_${idx}"], input[type="number"][name="killer_${idx}"], input[type="url"][name="killer_${idx}"]`) as HTMLInputElement | HTMLSelectElement | null;
          const otherTextEl = block.querySelector(`input[name="killer_${idx}_other_text"]`) as HTMLInputElement;
          const otherTextValue = otherTextEl?.value?.trim() || '';
          const answer =
            checkboxInputs.length > 0
              ? Array.from(checkboxInputs).map(cb => cb.value === '__other__' ? otherTextValue : cb.value).filter(Boolean).join(', ')
              : radioInput?.value === '__other__' ? otherTextValue
              : radioInput?.value || textareaInput?.value
              || (singleInput?.value === '__other__' ? otherTextValue : singleInput?.value)
              || '';
          if (question) {
            killerQuestions.push({ question, answer });
          }
        });

        // Backend expects 'killer_questions' field (JSON string). Send empty array if none
        formData.set('killer_questions', JSON.stringify(killerQuestions));

        // Add reCAPTCHA token
        formData.set('recaptcha_token', token);

        // Add tracking code if present in URL params
        if (trackingCode) {
          formData.set('tracking_code', trackingCode);
        }

        // Add candidate-selected source when the tracking link required it
        if (needsSourceSelection && trackingPlatform) {
          formData.set('tracking_platform', trackingPlatform);
        }

        // Forward UTM params captured from the landing URL (if any)
        appendUtmParamsToFormData(formData);

        // Use client UUID for apply endpoint as required by backend
        if (positionData) {
          if (isReferral) {
            await apiService.applyToPositionReferral(positionData.client_uuid, positionUuid!, formData);
          } else if (isInternal) {
            // Ensure email is always a string (backend rejects null)
            if (!formData.get('email')) {
              formData.set('email', '');
            }
            await apiService.applyToPositionInternal(positionData.client_uuid, positionUuid!, formData);
          } else {
            await apiService.applyToPosition(positionData.client_uuid, positionUuid!, formData);
          }
          setIsSubmitted(true);
          setSubmittedThisSession(true);

          // Skip the "already applied" cache on the referral portal so the
          // referrer can submit additional candidates to the same position.
          if (!isReferral) {
            const appliedPositions = JSON.parse(localStorage.getItem('appliedPositions') || '[]');
            if (!appliedPositions.includes(positionUuid)) {
              appliedPositions.push(positionUuid);
              localStorage.setItem('appliedPositions', JSON.stringify(appliedPositions));
            }
          }
        }
      } catch (err: any) {
        // Handle 401/403 for internal site — redirect to gate
        if (isGated && (err?.response?.status === 401 || err?.response?.status === 403)) {
          clearInternalToken();
          if (portalCode) {
            navigate(`${portalGatePath(isReferral, portalCode)}?returnTo=${encodeURIComponent(window.location.pathname)}`);
          }
          return;
        }
        // Show server error details if available
        const message = err?.response?.data?.detail || err?.message || (positionData?.translations?.['apply.error_network'] || 'Network error. Try again.');
        setFormError(Array.isArray(message) ? JSON.stringify(message) : String(message));
        // Reset reCAPTCHA to allow retry
        if ((window as any).grecaptcha?.enterprise?.reset) {
          (window as any).grecaptcha.enterprise.reset();
        }
      } finally {
        setIsSubmitting(false);
      }
    };

    // Load reCAPTCHA script AFTER callback is defined.
    // Skip when sitekey is missing (e.g. local dev): loading
    // enterprise.js?render=undefined still defines window.grecaptcha.enterprise,
    // which makes execute() hang and the click handler stuck on "Verifying...".
    let script: HTMLScriptElement | null = null;
    if (recaptchaSiteKey) {
      script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${recaptchaSiteKey}`;
      script.onerror = () => {
        // reCAPTCHA script failed to load
      };
      document.body.appendChild(script);
    }

    // Cleanup function
    return () => {
      delete (window as any).onSubmit;
      // Clean up script if component unmounts
      if (script?.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [positionData, selectedFile, selectedDocuments, positionUuid, recaptchaSiteKey, phoneNumber, isInternal, isReferral, isGated, portalCode, needsSourceSelection, trackingPlatform]);

  // Reset form state so the referrer can submit another candidate to the same
  // position without reloading the page.
  const resetReferralForm = () => {
    const formElement = document.getElementById('application-form') as HTMLFormElement | null;
    formElement?.reset();
    setIsSubmitted(false);
    setSubmittedThisSession(false);
    setFormError(null);
    setSelectedFile(null);
    setSelectedDocuments([]);
    setPhoneNumber('');
    setMultiChoiceSelections({});
    setOtherText({});
    setOtherSelected({});
    setSingleChoiceSelections({});
    if ((window as any).grecaptcha?.enterprise?.reset) {
      (window as any).grecaptcha.enterprise.reset();
    }
  };

  // Cambiar favicon dinámicamente
  useFavicon(positionData?.favicon);

  // Load Infojobs apply widget script and handler function
  useEffect(() => {
    // Only load if we have positionData and the API key
    if (!positionData || !infojobsApiKey) {
      return;
    }

    // Define the callback function globally (must be defined BEFORE loading the script)
    (window as any).handleProfileData = (_jsonResponse: any) => {
      // Process the jsonResponse here (e.g., populate form fields)
    };

    // Check if script is already loaded
    const existingScript = document.querySelector(`script[src="${infojobsWidgetSrc}"]`);
    if (existingScript) {
      return; // Script already loaded
    }

    // Load the Infojobs widget script
    const infojobsScript = document.createElement('script');
    infojobsScript.type = 'text/javascript';
    infojobsScript.src = infojobsWidgetSrc;
    infojobsScript.defer = true;
    infojobsScript.onerror = () => {
      // Infojobs apply widget script failed to load
    };
    document.body.appendChild(infojobsScript);

    // Cleanup function
    return () => {
      delete (window as any).handleProfileData;
      if (infojobsScript.parentNode) {
        infojobsScript.parentNode.removeChild(infojobsScript);
      }
    };
  }, [positionData, infojobsApiKey]);

  useEffect(() => {
    const fetchPositionData = async () => {
      if (!positionUuid) return;

      try {
        setLoading(true);

        // For gated mode (internal/referral), check if verification is needed
        let internalConfig: Awaited<ReturnType<typeof apiService.getInternalConfig>> | null = null;
        if (isGated && portalCode) {
          try {
            const config = isReferral
              ? await apiService.getReferralConfig(portalCode!)
              : await apiService.getInternalConfig(portalCode!);
            if (isReferral) {
              const referralConfig = config as Awaited<ReturnType<typeof apiService.getReferralConfig>>;
              setReferralRequireEmployeeId(referralConfig.require_employee_id ?? false);
              setReferralRequireReferrerIdentity(referralConfig.require_referrer_identity ?? false);
              setReferralIsCvRequired(referralConfig.is_cv_required ?? true);
              setReferralIsEmailRequired(referralConfig.is_email_required ?? true);
            } else {
              internalConfig = config as Awaited<ReturnType<typeof apiService.getInternalConfig>>;
            }
            if (config.requires_verification && !hasInternalToken()) {
              if (portalCode) {
                navigate(`${portalGatePath(isReferral, portalCode)}?returnTo=${encodeURIComponent(window.location.pathname)}`);
              }
              return;
            }
          } catch {
            // On error, redirect to gate as safe default
            if (!hasInternalToken() && portalCode) {
              navigate(`${portalGatePath(isReferral, portalCode)}?returnTo=${encodeURIComponent(window.location.pathname)}`);
            }
            return;
          }
        }

        let data: PositionData;
        if (isReferral) {
          data = await apiService.getReferralPositionData(positionUuid);
        } else if (isInternal) {
          data = await apiService.getInternalPositionData(positionUuid);
          // Only the internal config carries the email flag, not the position payload.
          if (internalConfig) {
            data.is_email_required = internalConfig.is_email_required;
            data.require_manager_confirmation = internalConfig.require_manager_confirmation ?? false;
            setInternalRequiresVerification(internalConfig.requires_verification);
          }
        } else {
          data = await apiService.getPositionData(positionUuid, apCode);
        }

        // Hostname validation: ensure the domain is allowed to serve this client
        if (!apCode && !validateHostname(data.custom_apply_domain, data.apply_url)) {
          navigate('/404', { replace: true });
          return;
        }

        // URL canonicalization: redirect to the well-formed URL if current URL doesn't match.
        // Skip for /sites/ URLs (apCode) — keep the apply site URL intact.
        // Skip in dev — canonical URL is always jobs.orbio.work which won't match localhost.
        // This handles: wrong slugs, path-mode URLs on custom domains, etc.
        if (!isGated && !apCode && !import.meta.env.DEV && data.apply_url) {
          const canonicalUrl = getCanonicalJobUrl(data.apply_url, data.custom_apply_domain);
          const currentUrl = `${window.location.origin}${window.location.pathname}`;
          if (canonicalUrl && canonicalUrl !== currentUrl) {
            // Redirect preserving query params (e.g. tracking_code)
            window.location.replace(canonicalUrl + window.location.search);
            return;
          }
        }

        setPositionData(data);

        // Set page title to position name
        document.title = data.position_title;

        // Inject JobPosting structured data (JSON-LD) for Google Job Search
        // Remove any previously injected JSON-LD to avoid duplicates on navigation
        document.querySelectorAll('script[data-job-posting-ld]').forEach(el => el.remove());
        const jobPostingLd: Record<string, unknown> = {
          '@context': 'https://schema.org',
          '@type': 'JobPosting',
          title: data.position_title,
          description: data.position_description,
          hiringOrganization: {
            '@type': 'Organization',
            name: data.company_name,
            ...(data.client_logo && { logo: data.client_logo }),
          },
          ...(data.date_posted && { datePosted: data.date_posted.slice(0, 10) }),
          ...(data.apply_url && { url: data.apply_url }),
          ...(data.location && {
            jobLocation: {
              '@type': 'Place',
              address: {
                '@type': 'PostalAddress',
                addressLocality: data.location,
                ...(data.country && { addressCountry: data.country }),
              },
            },
          }),
          ...(data.join_date && { jobStartDate: data.join_date }),
        };
        const ldScript = document.createElement('script');
        ldScript.type = 'application/ld+json';
        ldScript.setAttribute('data-job-posting-ld', 'true');
        ldScript.textContent = JSON.stringify(jobPostingLd);
        document.head.appendChild(ldScript);

        // Apply custom CSS from client data (this will override Tailwind's default colors)
        if (data.custom_css) {
          const style = document.createElement('style');
          style.textContent = data.custom_css;
          document.head.appendChild(style);

          // Extract brand color and apply to submit button
          setTimeout(() => {
            const el = document.createElement('h1');
            el.className = 'custom-color';
            el.style.display = 'none';
            document.body.appendChild(el);
            const color = getComputedStyle(el).color;
            document.body.removeChild(el);

            if (color && color !== 'rgb(0, 0, 0)') {
              const brandStyle = document.createElement('style');
              brandStyle.textContent = `
                button.custom-color { background-color: ${color} !important; }
                button.custom-color:hover { filter: brightness(0.9); }
                .attribute-badge { background-color: color-mix(in hsl, ${color} 15%, white); color: color-mix(in hsl, ${color} 70%, black); }
                .cv-upload-zone { border-color: ${color} !important; }
                .cv-upload-zone:hover { background-color: color-mix(in hsl, ${color} 5%, white) !important; }
                .cv-upload-icon { color: ${color} !important; background-color: color-mix(in hsl, ${color} 8%, white) !important; }
                .yn-btn-selected { border-color: ${color} !important; background-color: color-mix(in hsl, ${color} 6%, white) !important; color: ${color} !important; }
                .accent-link { color: ${color} !important; }
                .accent-checkbox { accent-color: ${color} !important; }
              `;
              document.head.appendChild(brandStyle);
            }
          }, 0);
        }

        // Add animation styles
        const animationStyle = document.createElement('style');
        animationStyle.textContent = `
          @keyframes fade-in {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }
          .animate-fade-in {
            animation: fade-in 0.2s ease-out;
          }

          /* PhoneInput component styling */
          .react-international-phone-input-container {
            display: flex !important;
            width: 100% !important;
            border: 1px solid #d1d5db !important;
            border-radius: 0.5rem !important;
            background: white !important;
            height: 42px !important;
            align-items: center !important;
            position: relative !important;
            overflow: visible !important;
          }

          .react-international-phone-input-container:focus-within {
            border-color: #7d8c70 !important;
            box-shadow: 0 0 0 3px rgba(124, 193, 158, 0.14) !important;
          }

          .react-international-phone-country-selector {
            display: flex !important;
            align-items: center !important;
            padding: 0 0.5rem !important;
            background: #f9fafb !important;
            border-right: 1px solid #d1d5db !important;
            cursor: pointer !important;
            min-width: 80px !important;
            height: 100% !important;
            border-radius: 0.5rem 0 0 0.5rem !important;
            position: relative !important;
            z-index: 10 !important;
          }

          .react-international-phone-country-selector:hover {
            background: #f3f4f6 !important;
          }

          .react-international-phone-country-selector-button {
            display: flex !important;
            align-items: center !important;
            gap: 0.25rem !important;
            background: none !important;
            border: none !important;
            padding: 0 !important;
            width: 100% !important;
            font-size: 0.875rem !important;
            height: 100% !important;
          }

          .react-international-phone-input {
            flex: 1 !important;
            border: none !important;
            outline: none !important;
            padding: 0.5rem 0.75rem !important;
            font-size: 0.875rem !important;
            background: white !important;
            height: 100% !important;
            border-radius: 0 0.5rem 0.5rem 0 !important;
          }

          .react-international-phone-input:focus {
            box-shadow: none !important;
          }

          .react-international-phone-country-selector-flag {
            width: 20px !important;
            height: 15px !important;
            object-fit: cover !important;
          }

          .react-international-phone-country-selector-dropdown {
            position: absolute !important;
            top: calc(100% + 2px) !important;
            left: 0 !important;
            right: 0 !important;
            background: white !important;
            border: 1px solid #d1d5db !important;
            border-radius: 0.5rem !important;
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
            z-index: 9999 !important;
            max-height: 200px !important;
            overflow-y: auto !important;
            min-width: 200px !important;
          }

          .react-international-phone-country-selector-dropdown-item {
            display: flex !important;
            align-items: center !important;
            gap: 0.5rem !important;
            padding: 0.5rem 0.75rem !important;
            cursor: pointer !important;
            font-size: 0.875rem !important;
            transition: background-color 0.15s ease !important;
          }

          .react-international-phone-country-selector-dropdown-item:hover {
            background: #f3f4f6 !important;
          }

          .react-international-phone-country-selector-dropdown-item:first-child {
            border-radius: 0.5rem 0.5rem 0 0 !important;
          }

          .react-international-phone-country-selector-dropdown-item:last-child {
            border-radius: 0 0 0.5rem 0.5rem !important;
          }
        `;
        document.head.appendChild(animationStyle);

        // Apply custom JS if available — replace any previously injected script
        customJsScriptRef.current?.remove();
        customJsScriptRef.current = null;
        if (data.custom_js) {
          const script = document.createElement('script');
          script.textContent = data.custom_js;
          document.body.appendChild(script);
          customJsScriptRef.current = script;
        }
      } catch (err: any) {
        // Handle 401/403 for internal site — redirect to gate
        if (isGated && (err?.response?.status === 401 || err?.response?.status === 403)) {
          clearInternalToken();
          if (portalCode) {
            navigate(`${portalGatePath(isReferral, portalCode)}?returnTo=${encodeURIComponent(window.location.pathname)}`);
          }
          return;
        }
        setError(`Error loading position data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchPositionData();
  }, [positionUuid, isInternal, isReferral, isGated, portalCode, clientUuid, apCode]);


  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
  };

  const handleDropAreaClick = () => {
    document.getElementById('cv-upload')?.click();
  };

  const handleDocumentInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const max = positionData?.apply_site_document_upload_max_files ?? 3;
      setSelectedDocuments(Array.from(e.target.files).slice(0, max));
    }
  };

  const handleDocumentDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files) {
      const max = positionData?.apply_site_document_upload_max_files ?? 3;
      setSelectedDocuments(Array.from(e.dataTransfer.files).slice(0, max));
    }
  };

  // Build back link based on internal/public mode
  const getBackLink = () => {
    if (apCode) {
      return `/sites/${apCode}`;
    }
    if (isReferral && portalCode) {
      return `/referral/${portalCode}`;
    }
    if (isInternal && portalCode) {
      return `/internal/${portalCode}`;
    }
    if (positionData) {
      return isOnCustomDomain
        ? '/'
        : `/${positionData.client_code}`;
    }
    return '/';
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafaf7]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3d4a2e] mx-auto mb-4"></div>
          <p className="text-[#666d5a]">{loadingTranslations['apply.loading'] || 'Loading position details...'}</p>
        </div>
      </div>
    );
  }

  if (error || !positionData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafaf7]">
        <div className="text-center">
          <h1 className="font-serif text-[28px] font-normal text-[#1e2418] mb-4">Position Not Found</h1>
          <p className="text-[#666d5a]">{loadingTranslations['apply.position_not_found'] || "The position you're looking for could not be found."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#fafaf7] min-h-screen flex flex-col antialiased">
      {/* Header */}
      <header className="w-full flex items-center justify-between px-6 py-4 bg-white border-b border-[#dde0d4]">
        {positionData.client_logo ? (
          <a href={getBackLink()} className="hover:opacity-80 transition-opacity">
            <img
              src={positionData.client_logo}
              alt="Company logo"
              className="h-10 max-w-[120px] object-contain"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </a>
        ) : (
          <a href={getBackLink()} className="hover:opacity-80 transition-opacity">
            <div className="h-10 w-32 bg-[#dde0d4] rounded flex items-center justify-center text-[#666d5a] text-sm">
              {positionData?.translations?.['apply.no_logo'] || 'No Logo'}
            </div>
          </a>
        )}
      </header>

      <main className="flex-1 w-full">
      {/* Company Image Banner */}
      {positionData.company_image && (
        <div className="w-full">
          <img
            src={positionData.company_image}
            alt="Company image"
            className="w-full max-h-64 object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        </div>
      )}

      {/* Main Content */}
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
          {/* Back link */}
          <a
            href={getBackLink()}
            className="inline-flex items-center gap-1.5 text-[13px] text-[#666d5a] hover:text-[#1e2418] transition-colors mb-6"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 5l-7 7 7 7"/>
            </svg>
            {positionData.translations?.['apply.back'] || 'All open roles'}
          </a>

          <div className="flex flex-col md:flex-row md:gap-16 md:items-start">
            {/* Position Details */}
            <section className="flex-1 min-w-0">
              <div className="mb-8">
                <div className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#666d5a] mb-3">
                  {positionData.translations?.['apply.new_opportunity'] || 'New opportunity'}
                </div>
                <h1 className="font-serif text-[38px] md:text-[48px] font-normal text-[#1e2418] leading-[1.05] tracking-[-0.01em] mb-5">
                  {positionData.position_title || 'No Position Title'}
                </h1>
                <div className="flex flex-wrap gap-5 mb-5 text-[14px] text-[#555c4a]">
                  {positionData.location && (
                    <span className="flex items-center gap-2">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 010-5 2.5 2.5 0 010 5z"/>
                      </svg>
                      {positionData.location}
                    </span>
                  )}
                  {positionData.join_date && (
                    <span className="flex items-center gap-2">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                      {positionData.translations?.['apply.join_date_label'] || 'Start date:'} {positionData.join_date}
                    </span>
                  )}
                  {positionData.attributes?.map((attr, i) => (
                    <span key={i} className="attribute-badge inline-flex items-center px-2 py-0.5 text-xs rounded-full font-medium">
                      {attr.value}
                    </span>
                  ))}
                </div>
                <div
                  className="prose text-[#555c4a] text-[15px] leading-[1.7] mb-4 overflow-hidden break-words"
                  dangerouslySetInnerHTML={{ __html: (positionData.position_description || 'No position description available.').replace(/^\s*<h[1-3][^>]*>.*?<\/h[1-3]>\s*/is, '') }}
                />
              </div>
            </section>

            {/* Application Form / Success Card */}
            <aside
              ref={applicationAsideRef}
              className={`w-full md:w-[400px] flex-shrink-0 ${canStickApplicationForm ? 'md:sticky md:top-8' : ''}`}
            >
              {!isSubmitted ? (
              <form
                id="application-form"
                className="bg-[#edefe8] rounded-xl border border-[#dde0d4] p-6 mb-8 md:mb-0 space-y-4"
                style={{ overflow: 'visible' }}
                autoComplete="off"
              >
                <input type="hidden" name="csrf_access_token" value={positionData.csrf_token} />

                <div className="mb-1">
                  <div className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#666d5a] mb-2">
                    {positionData.translations?.['apply.form_eyebrow'] || 'Apply · ~2 minutes'}
                  </div>
                  <h2 className="font-serif text-[24px] font-normal text-[#1e2418] leading-tight tracking-[-0.01em]">
                    {isReferral
                      ? (positionData.translations?.['apply.referral_form_title'] || 'Refer a candidate')
                      : positionData.translations?.['apply.form_title']}
                  </h2>
                </div>

                <div>
                  <label htmlFor="first-name" className="block text-[13px] font-medium text-[#1e2418] mb-1.5">
                    {isReferral
                      ? positionData.translations?.['apply.referral_first_name']
                      : positionData.translations?.['apply.full_name']}
                  </label>
                  <input
                    id="first-name"
                    name="first_name"
                    type="text"
                    required
                    maxLength={100}
                    className="w-full px-3 py-2 bg-white border border-[#dde0d4] rounded-lg text-[14px] text-[#1e2418] focus:outline-none focus:border-[#7d8c70] focus:shadow-[0_0_0_3px_rgba(124,193,158,0.14)] transition"
                  />
                </div>

                <div>
                  <label htmlFor="last-name" className="block text-[13px] font-medium text-[#1e2418] mb-1.5">
                    {isReferral
                      ? positionData.translations?.['apply.referral_last_name']
                      : positionData.translations?.['apply.last_name']}
                  </label>
                  <input
                    id="last-name"
                    name="last_name"
                    type="text"
                    required
                    maxLength={100}
                    className="w-full px-3 py-2 bg-white border border-[#dde0d4] rounded-lg text-[14px] text-[#1e2418] focus:outline-none focus:border-[#7d8c70] focus:shadow-[0_0_0_3px_rgba(124,193,158,0.14)] transition"
                  />
                </div>

                <div className="relative" style={{ overflow: 'visible', zIndex: 1 }}>
                  <label htmlFor="phone" className="block text-[13px] font-medium text-[#1e2418] mb-1.5">
                    {isReferral
                      ? positionData.translations?.['apply.referral_phone']
                      : positionData.translations?.['apply.phone']}
                  </label>
                  <PhoneInput
                    defaultCountry={positionData.country.toLowerCase()}
                    value={phoneNumber}
                    onChange={(value) => {
                      setPhoneNumber(value.toString());
                    }}
                    inputProps={{
                      id: 'phone',
                      name: 'phone',
                      required: true,
                      'aria-invalid': phoneNumber.replace(/\D/g, '').length > 3 && !isPhoneValid(phoneNumber),
                      'aria-describedby': phoneNumber.replace(/\D/g, '').length > 3 && !isPhoneValid(phoneNumber) ? 'phone-error' : undefined,
                      className: 'w-full px-3 py-2 border-0 focus:outline-none focus:ring-0'
                    }}
                  />
                  {phoneNumber && phoneNumber.replace(/\D/g, '').length > 3 && !isPhoneValid(phoneNumber) && (
                    <div id="phone-error" className="text-red-500 text-xs mt-1">
                      {positionData.translations?.['apply.phone_invalid'] || 'Invalid phone number'}
                    </div>
                  )}
                </div>

                {(!isInternal || (positionData.is_email_required && !internalRequiresVerification)) && (
                  <div>
                    <label htmlFor="email" className="block text-[13px] font-medium text-[#1e2418] mb-1.5">
                      {isReferral
                        ? positionData.translations?.['apply.referral_email']
                        : (positionData.translations?.['apply.email'] || 'Email')}
                      {!(isReferral ? getReferralIsEmailRequired() : positionData.is_email_required) && (
                        <span className="text-[#666d5a] font-normal ml-1">{positionData.translations?.['apply.optional'] || '(Opcional)'}</span>
                      )}
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required={isReferral ? getReferralIsEmailRequired() : positionData.is_email_required}
                      maxLength={254}
                      className="w-full px-3 py-2 bg-white border border-[#dde0d4] rounded-lg text-[14px] text-[#1e2418] focus:outline-none focus:border-[#7d8c70] focus:shadow-[0_0_0_3px_rgba(124,193,158,0.14)] transition"
                    />
                  </div>
                )}

                {!isGated && positionData.ask_referrer_on_apply && (
                  <div>
                    <label htmlFor="referred-by" className="block text-[13px] font-medium text-[#1e2418] mb-1.5">
                      {positionData.translations?.['apply.referred_by'] || 'Who referred you?'}
                      <span className="text-[#666d5a] font-normal ml-1">{positionData.translations?.['apply.optional'] || '(Optional)'}</span>
                    </label>
                    <input
                      id="referred-by"
                      name="referred_by"
                      type="email"
                      maxLength={254}
                      placeholder={positionData.translations?.['apply.referred_by_hint'] || "Referrer's email"}
                      className="w-full px-3 py-2 bg-white border border-[#dde0d4] rounded-lg text-[14px] text-[#1e2418] focus:outline-none focus:border-[#7d8c70] focus:shadow-[0_0_0_3px_rgba(124,193,158,0.14)] transition"
                    />
                  </div>
                )}


                {needsSourceSelection && positionData.tracking_sources.length > 0 && (
                  <div>
                    <label htmlFor="tracking-source" className="block text-sm font-medium text-gray-700 mb-1">
                      {positionData.translations?.['apply.source_label'] || 'How did you hear about us?'}
                    </label>
                    <select
                      id="tracking-source"
                      value={trackingPlatform}
                      onChange={(e) => setTrackingPlatform(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    >
                      <option value="" disabled>
                        {positionData.translations?.['apply.source_placeholder'] || 'Select a source'}
                      </option>
                      {positionData.tracking_sources.map((src) => (
                        <option key={src} value={src}>{src}</option>
                      ))}
                    </select>
                  </div>
                )}

                {isReferral && getReferralRequireReferrerIdentity() && (
                  <>
                    <div>
                      <label htmlFor="referrer-name" className="block text-[13px] font-medium text-[#1e2418] mb-1.5">
                        {positionData.translations?.['apply.referrer_name'] || "Referrer's Full Name"}
                      </label>
                      <input
                        id="referrer-name"
                        name="referrer_name"
                        type="text"
                        required
                        maxLength={200}
                        className="w-full px-3 py-2 bg-white border border-[#dde0d4] rounded-lg text-[14px] text-[#1e2418] focus:outline-none focus:border-[#7d8c70] focus:shadow-[0_0_0_3px_rgba(124,193,158,0.14)] transition"
                      />
                    </div>
                    <div>
                      <label htmlFor="referrer-national-id" className="block text-[13px] font-medium text-[#1e2418] mb-1.5">
                        {positionData.translations?.['apply.referrer_national_id'] || "Referrer's National ID (VAT, DNI, NIE, etc)"}
                      </label>
                      <input
                        id="referrer-national-id"
                        name="employee_id"
                        type="text"
                        required
                        maxLength={100}
                        pattern="[a-zA-Z0-9\-_]+"
                        title={positionData.translations?.['apply.employee_id_hint'] || 'Only letters, numbers, hyphens and underscores'}
                        className="w-full px-3 py-2 bg-white border border-[#dde0d4] rounded-lg text-[14px] text-[#1e2418] focus:outline-none focus:border-[#7d8c70] focus:shadow-[0_0_0_3px_rgba(124,193,158,0.14)] transition"
                      />
                    </div>
                  </>
                )}

                {isReferral && !getReferralRequireReferrerIdentity() && getReferralRequireEmployeeId() && (
                  <div>
                    <label htmlFor="employee-id" className="block text-[13px] font-medium text-[#1e2418] mb-1.5">
                      {positionData.translations?.['apply.employee_id'] || 'Employee ID'}
                    </label>
                    <input
                      id="employee-id"
                      name="employee_id"
                      type="text"
                      required
                      maxLength={100}
                      pattern="[a-zA-Z0-9\-_]+"
                      title={positionData.translations?.['apply.employee_id_hint'] || 'Only letters, numbers, hyphens and underscores'}
                      className="w-full px-3 py-2 bg-white border border-[#dde0d4] rounded-lg text-[14px] text-[#1e2418] focus:outline-none focus:border-[#7d8c70] focus:shadow-[0_0_0_3px_rgba(124,193,158,0.14)] transition"
                    />
                  </div>
                )}

                <div>
                  <label htmlFor="cv-upload" className="block text-[13px] font-medium text-[#1e2418] mb-1.5">
                    {positionData.translations?.['apply.cv']}
                    {!(isReferral ? getReferralIsCvRequired() : positionData.is_cv_required) && (
                      <span className="text-[#666d5a] font-normal ml-1">{positionData.translations?.['apply.optional'] || '(Opcional)'}</span>
                    )}
                  </label>
                  <button
                    type="button"
                    className="cv-upload-zone flex flex-col items-center justify-center border-[1.5px] border-dashed border-[#dde0d4] rounded-lg p-5 text-center cursor-pointer transition bg-white hover:bg-[#f5f6f1] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3d4a2e]"
                    onClick={handleDropAreaClick}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                  >
                    <span className="cv-upload-icon w-9 h-9 rounded-full bg-[#f0f1ec] flex items-center justify-center mb-2 text-[#666d5a]">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 16v-8m0 0l-4 4m4-4l4 4"/>
                        <path d="M20 16.58A5 5 0 0018 7h-1.26A8 8 0 104 15.25"/>
                      </svg>
                    </span>
                    <span className="text-[13.5px] text-[#1e2418]">
                      {positionData.translations?.['apply.cv_drop']}
                      {!(isReferral ? getReferralIsCvRequired() : positionData.is_cv_required) && ` ${positionData.translations?.['apply.optional'] || '(Opcional)'}`}
                    </span>
                    <span className="block text-[11.5px] text-[#666d5a] mt-0.5">
                      {positionData.translations?.['apply.cv_formats'] || 'PDF, DOCX, RTF o imágenes (máx 5MB)'}
                    </span>
                    {selectedFile && (
                      <span className="block text-xs text-[#555c4a] mt-2">
                        {selectedFile.name} ({Math.round(selectedFile.size / 1024)} KB)
                        {['jpg', 'jpeg', 'png'].includes(selectedFile.name.split('.').pop()?.toLowerCase() || '') && (
                          <span className="block text-orange-600 text-xs font-medium mt-1">
                            {positionData.translations?.['apply.cv_image_warning']}
                          </span>
                        )}
                      </span>
                    )}
                  </button>
                  <input
                    id="cv-upload"
                    name="cv"
                    type="file"
                    accept=".pdf,.doc,.docx,.rtf,.jpg,.jpeg,.png"
                    className="hidden"
                    onChange={handleFileInputChange}
                  />
                  <span className="sr-only" role="status">{selectedFile?.name || ''}</span>
                </div>

                {/* Additional Documents */}
                {!isGated && positionData.apply_site_document_upload_enabled && (
                  <div>
                    <label htmlFor="documents-upload" className="block text-[13px] font-medium text-[#1e2418] mb-1.5">
                      {positionData.translations?.['apply.documents']}
                    </label>
                    {positionData.apply_site_document_upload_instructions && (
                      <p className="text-xs text-[#666d5a] mb-2">
                        {positionData.apply_site_document_upload_instructions}
                      </p>
                    )}
                    <button
                      type="button"
                      className="cv-upload-zone flex flex-col items-center justify-center border-[1.5px] border-dashed border-[#dde0d4] rounded-lg p-5 text-center cursor-pointer transition bg-white hover:bg-[#f5f6f1] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3d4a2e]"
                      onClick={() => document.getElementById('documents-upload')?.click()}
                      onDrop={handleDocumentDrop}
                      onDragOver={handleDragOver}
                    >
                      <span className="cv-upload-icon w-9 h-9 rounded-full bg-[#f0f1ec] flex items-center justify-center mb-2 text-[#666d5a]">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 16v-8m0 0l-4 4m4-4l4 4"/>
                          <path d="M20 16.58A5 5 0 0018 7h-1.26A8 8 0 104 15.25"/>
                        </svg>
                      </span>
                      <span className="text-[13.5px] text-[#1e2418]">
                        {positionData.translations?.['apply.documents_drop']}
                      </span>
                      {selectedDocuments.length > 0 && (
                        <span className="block text-xs text-[#555c4a] mt-2 space-y-1">
                          {selectedDocuments.map((doc, i) => (
                            <span className="block" key={i}>{doc.name} ({Math.round(doc.size / 1024)} KB)</span>
                          ))}
                        </span>
                      )}
                    </button>
                    <input
                      id="documents-upload"
                      type="file"
                      multiple
                      className="hidden"
                      onChange={handleDocumentInputChange}
                    />
                    <span className="sr-only" role="status">{selectedDocuments.map(doc => doc.name).join(', ')}</span>
                  </div>
                )}

                {/* Killer Questions */}
                {positionData.questions && positionData.questions.length > 0 && (
                  <div className="bg-white border border-[#dde0d4] rounded-lg overflow-hidden">
                    <div className="px-4 pt-4 pb-3">
                      <span className="block text-[13.5px] font-semibold text-[#1e2418]">
                        {positionData.translations?.['apply.required_questions'] || 'Required questions'}
                      </span>
                    </div>
                    <div>
                      {positionData.questions.map((q: ApplySiteQuestion, idx: number) => (
                        <div key={idx} data-killer-idx={idx} className="px-4 py-3 border-t border-[#dde0d4]">
                          <p id={`killer-question-${idx}`} className="text-[13.5px] text-[#1e2418] leading-[1.4] mb-2">
                            {q.question}
                          </p>
                          {q.type === 'free_text' ? (
                            <textarea
                              aria-labelledby={`killer-question-${idx}`}
                              name={`killer_${idx}`}
                              required
                              minLength={q.min_chars ?? 10}
                              maxLength={q.max_chars ?? 5000}
                              className="w-full px-3 py-2 text-sm bg-white border border-[#dde0d4] rounded-lg text-[#1e2418] resize-y focus:outline-none focus:border-[#7d8c70] focus:shadow-[0_0_0_3px_rgba(124,193,158,0.14)]"
                              rows={4}
                            />
                          ) : q.type === 'multi_choice' ? (
                            <>
                              <div className="flex flex-wrap gap-2">
                                {(q.answers || []).map((answer: string, answerIdx: number) => (
                                  <label key={answerIdx} className="relative cursor-pointer">
                                    <input
                                      type="checkbox"
                                      name={`killer_${idx}`}
                                      value={answer}
                                      checked={(multiChoiceSelections[idx] || []).includes(answer)}
                                      onChange={(e) => {
                                        const sel = multiChoiceSelections[idx] || [];
                                        const next = e.target.checked
                                          ? [...sel, answer]
                                          : sel.filter(v => v !== answer);
                                        setMultiChoiceSelections(prev => ({ ...prev, [idx]: next }));
                                      }}
                                      className="peer sr-only"
                                    />
                                    <span className="inline-block px-3 py-1.5 text-xs border border-[#dde0d4] rounded-lg text-[#1e2418] bg-white transition-all peer-checked:bg-[#1e2418] peer-checked:border-[#1e2418] peer-checked:text-white hover:border-[#7d8c70] hover:bg-[#f5f6f1]">
                                      {answer}
                                    </span>
                                  </label>
                                ))}
                                {q.has_other_option && (
                                  <label className="relative cursor-pointer">
                                    <input
                                      type="checkbox"
                                      name={`killer_${idx}`}
                                      value="__other__"
                                      checked={(multiChoiceSelections[idx] || []).includes('__other__')}
                                      onChange={(e) => {
                                        const sel = multiChoiceSelections[idx] || [];
                                        const next = e.target.checked
                                          ? [...sel, '__other__']
                                          : sel.filter(v => v !== '__other__');
                                        setMultiChoiceSelections(prev => ({ ...prev, [idx]: next }));
                                      }}
                                      className="peer sr-only"
                                    />
                                    <span className="inline-block px-3 py-1.5 text-xs border border-[#dde0d4] rounded-lg text-[#1e2418] bg-white transition-all peer-checked:bg-[#1e2418] peer-checked:border-[#1e2418] peer-checked:text-white hover:border-[#7d8c70] hover:bg-[#f5f6f1]">
                                      {positionData.translations['apply.other_option']}
                                    </span>
                                  </label>
                                )}
                              </div>
                              {q.has_other_option && (multiChoiceSelections[idx] || []).includes('__other__') && (
                                <input
                                  type="text"
                                  value={otherText[idx] || ''}
                                  onChange={(e) => setOtherText(prev => ({ ...prev, [idx]: e.target.value }))}
                                  required
                                  className="w-full mt-2 px-3 py-2 text-sm bg-white border border-[#dde0d4] rounded-lg text-[#1e2418] focus:outline-none focus:border-[#7d8c70]"
                                />
                              )}
                              <input
                                type="text"
                                required
                                tabIndex={-1}
                                name={`killer_${idx}_mc`}
                                value={(multiChoiceSelections[idx] || []).length > 0 ? 'selected' : ''}
                                onChange={() => {}}
                                className="sr-only"
                              />
                              {q.has_other_option && (
                                <input type="hidden" name={`killer_${idx}_other_text`} value={otherText[idx] || ''} />
                              )}
                            </>
                          ) : q.type === 'date' ? (
                            <input
                              aria-labelledby={`killer-question-${idx}`}
                              type="date"
                              name={`killer_${idx}`}
                              required
                              className="w-full px-3 py-2 text-sm bg-white border border-[#dde0d4] rounded-lg text-[#1e2418] focus:outline-none focus:border-[#7d8c70]"
                            />
                          ) : q.type === 'numerical' ? (
                            <input
                              aria-labelledby={`killer-question-${idx}`}
                              type="number"
                              name={`killer_${idx}`}
                              required
                              min={q.min_value}
                              max={q.max_value}
                              className="w-full px-3 py-2 text-sm bg-white border border-[#dde0d4] rounded-lg text-[#1e2418] focus:outline-none focus:border-[#7d8c70]"
                            />
                          ) : q.type === 'yes_no' ? (
                            <div className="flex gap-2">
                              {[positionData.translations['apply.yes'], positionData.translations['apply.no']].map((answer) => (
                                <label key={answer} className="relative cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`killer_${idx}`}
                                    value={answer}
                                    required
                                    onChange={() => setSingleChoiceSelections(prev => ({ ...prev, [idx]: answer }))}
                                    className="sr-only"
                                  />
                                  <span className={`inline-block px-[18px] py-[6px] rounded-lg border text-[13px] font-medium transition-all ${
                                    singleChoiceSelections[idx] === answer
                                      ? 'yn-btn-selected border-[#3d4a2e] bg-[rgba(61,74,46,0.06)] text-[#3d4a2e] font-semibold'
                                      : 'border-[#dde0d4] bg-white text-[#1e2418] hover:border-[#7d8c70] hover:bg-[#f5f6f1]'
                                  }`}>
                                    {answer}
                                  </span>
                                </label>
                              ))}
                            </div>
                          ) : q.type === 'url' ? (
                            <input
                              aria-labelledby={`killer-question-${idx}`}
                              type="url"
                              name={`killer_${idx}`}
                              required
                              placeholder={q.placeholder || positionData.translations['apply.url_placeholder']}
                              className="w-full px-3 py-2 text-sm bg-white border border-[#dde0d4] rounded-lg text-[#1e2418] focus:outline-none focus:border-[#7d8c70]"
                            />
                          ) : (q.answers || []).length > 5 ? (
                            <>
                              <select
                                aria-labelledby={`killer-question-${idx}`}
                                name={`killer_${idx}`}
                                required
                                defaultValue=""
                                onChange={(e) => setOtherSelected(prev => ({ ...prev, [idx]: e.target.value === '__other__' }))}
                                className="orbio-select w-full pl-3 pr-9 py-2 text-sm bg-white border border-[#dde0d4] rounded-lg text-[#1e2418] focus:outline-none focus:border-[#7d8c70]"
                              >
                                <option value="" disabled>{positionData.translations['apply.select_placeholder']}</option>
                                {(q.answers || []).map((answer: string, answerIdx: number) => (
                                  <option key={answerIdx} value={answer}>{answer}</option>
                                ))}
                                {q.has_other_option && (
                                  <option value="__other__">{positionData.translations['apply.other_option']}</option>
                                )}
                              </select>
                              {q.has_other_option && otherSelected[idx] && (
                                <input
                                  type="text"
                                  value={otherText[idx] || ''}
                                  onChange={(e) => setOtherText(prev => ({ ...prev, [idx]: e.target.value }))}
                                  required
                                  className="w-full mt-2 px-3 py-2 text-sm bg-white border border-[#dde0d4] rounded-lg text-[#1e2418] focus:outline-none focus:border-[#7d8c70]"
                                />
                              )}
                              {q.has_other_option && (
                                <input type="hidden" name={`killer_${idx}_other_text`} value={otherText[idx] || ''} />
                              )}
                            </>
                          ) : (
                            <>
                              <div className="flex flex-wrap gap-2">
                                {(q.answers || []).map((answer: string, answerIdx: number) => (
                                  <label key={answerIdx} className="relative cursor-pointer">
                                    <input
                                      type="radio"
                                      name={`killer_${idx}`}
                                      value={answer}
                                      required
                                      onChange={() => {
                                        setOtherSelected(prev => ({ ...prev, [idx]: false }));
                                        setSingleChoiceSelections(prev => ({ ...prev, [idx]: answer }));
                                      }}
                                      className="peer sr-only"
                                    />
                                    <span className={`inline-block px-3 py-1.5 text-xs border border-[#dde0d4] rounded-lg text-[#1e2418] bg-white transition-all peer-checked:bg-[#1e2418] peer-checked:border-[#1e2418] peer-checked:text-white hover:border-[#7d8c70] hover:bg-[#f5f6f1] ${singleChoiceSelections[idx] && singleChoiceSelections[idx] !== answer ? 'opacity-40' : ''}`}>
                                      {answer}
                                    </span>
                                  </label>
                                ))}
                                {q.has_other_option && (
                                  <label className="relative cursor-pointer">
                                    <input
                                      type="radio"
                                      name={`killer_${idx}`}
                                      value="__other__"
                                      required
                                      onChange={() => {
                                        setOtherSelected(prev => ({ ...prev, [idx]: true }));
                                        setSingleChoiceSelections(prev => ({ ...prev, [idx]: '__other__' }));
                                      }}
                                      className="peer sr-only"
                                    />
                                    <span className={`inline-block px-3 py-1.5 text-xs border border-[#dde0d4] rounded-lg text-[#1e2418] bg-white transition-all peer-checked:bg-[#1e2418] peer-checked:border-[#1e2418] peer-checked:text-white hover:border-[#7d8c70] hover:bg-[#f5f6f1] ${singleChoiceSelections[idx] && singleChoiceSelections[idx] !== '__other__' ? 'opacity-40' : ''}`}>
                                      {positionData.translations['apply.other_option']}
                                    </span>
                                  </label>
                                )}
                              </div>
                              {q.has_other_option && otherSelected[idx] && (
                                <input
                                  type="text"
                                  value={otherText[idx] || ''}
                                  onChange={(e) => setOtherText(prev => ({ ...prev, [idx]: e.target.value }))}
                                  required
                                  className="w-full mt-2 px-3 py-2 text-sm bg-white border border-[#dde0d4] rounded-lg text-[#1e2418] focus:outline-none focus:border-[#7d8c70]"
                                />
                              )}
                              {q.has_other_option && (
                                <input type="hidden" name={`killer_${idx}_other_text`} value={otherText[idx] || ''} />
                              )}
                            </>
                          )}
                          <input
                            type="hidden"
                            name={`killer_question_text_${idx}`}
                            value={q.question}
                          />
                        </div>
                      ))}
                    </div>
                    <input type="hidden" name="killer_questions_json" />
                  </div>
                )}

                <div className="flex items-start gap-2.5 pt-1">
                  <input
                    id="privacy-policy"
                    name="privacy_policy"
                    type="checkbox"
                    required
                    className="accent-checkbox mt-0.5 w-3.5 h-3.5"
                    style={{ accentColor: '#3d4a2e' }}
                  />
                  <label htmlFor="privacy-policy" className="text-[12.5px] text-[#555c4a] select-none leading-[1.5]">
                    {positionData.translations?.['apply.privacy_policy'] || 'I accept the'}{' '}
                    <button
                      type="button"
                      onClick={() => setShowPrivacyModal(true)}
                      className="accent-link underline bg-transparent border-none p-0 cursor-pointer text-[#3d4a2e] hover:text-[#1e2418] transition"
                    >
                      {positionData.translations?.['apply.privacy_policy_link'] || 'Privacy policy'}
                    </button>
                    {positionData.terms_of_service && (
                      <>
                        {' '}
                        {positionData.translations?.['apply.privacy_and_tos_connector'] || 'and the'}{' '}
                        <button
                          type="button"
                          onClick={() => setShowTermsModal(true)}
                          className="accent-link underline bg-transparent border-none p-0 cursor-pointer text-[#3d4a2e] hover:text-[#1e2418] transition"
                        >
                          {positionData.translations?.['apply.terms_of_service_link'] || 'Terms of service'}
                        </button>
                      </>
                    )}
                  </label>
                </div>

                {isInternal && positionData.require_manager_confirmation && (
                  <div className="flex items-start gap-2.5 pt-1">
                    <input
                      id="manager-informed"
                      name="manager_informed"
                      type="checkbox"
                      value="true"
                      required
                      className="accent-checkbox mt-0.5 w-3.5 h-3.5"
                      style={{ accentColor: '#3d4a2e' }}
                    />
                    <label htmlFor="manager-informed" className="text-[12.5px] text-[#555c4a] select-none leading-[1.5]">
                      {positionData.translations?.['apply.manager_informed'] || 'I confirm that I have informed my current manager.'}
                    </label>
                  </div>
                )}

                {positionData.is_contact_consent_required && (
                  <div className="flex items-start gap-2.5 pt-1">
                    <input
                      id="contact-consent"
                      name="contact_consent"
                      type="checkbox"
                      required
                      className="accent-checkbox mt-0.5 w-3.5 h-3.5"
                      style={{ accentColor: '#3d4a2e' }}
                    />
                    <label htmlFor="contact-consent" className="text-[12.5px] text-[#555c4a] select-none leading-[1.5]">
                      {positionData.translations?.['apply.contact_consent'] || 'By providing your phone number, you agree to'}{' '}
                      <button
                        type="button"
                        onClick={() => setShowContactConsentModal(true)}
                        className="accent-link underline bg-transparent border-none p-0 cursor-pointer text-[#3d4a2e] hover:text-[#1e2418] transition"
                      >
                        {positionData.translations?.['apply.contact_consent_link'] || 'these contact terms'}
                      </button>
                      .
                    </label>
                  </div>
                )}

                {positionData.show_sms_consent && (
                  <div className="flex items-start gap-2.5 pt-1">
                    <input
                      id="sms-consent"
                      name="sms_consent"
                      type="checkbox"
                      required={positionData.is_sms_consent_required}
                      className="accent-checkbox mt-0.5 w-3.5 h-3.5"
                      style={{ accentColor: '#3d4a2e' }}
                    />
                    <label htmlFor="sms-consent" className="text-[12.5px] text-[#555c4a] select-none leading-[1.5]">
                      {(
                        positionData.translations?.['apply.sms_consent'] ||
                        'I agree to receive recruitment-related text messages (SMS/RCS) from {company} about my application, such as screening questions, interview scheduling, and application status updates. Message frequency varies. Message and data rates may apply. Reply HELP for help, Reply STOP to cancel. See the'
                      ).replace('{company}', positionData.company_name || '')}{' '}
                      <button
                        type="button"
                        onClick={() => setShowSmsConsentModal(true)}
                        className="accent-link underline bg-transparent border-none p-0 cursor-pointer text-[#3d4a2e] hover:text-[#1e2418] transition"
                      >
                        {positionData.translations?.['apply.sms_consent_link'] || 'SMS terms'}
                      </button>
                      {'. '}
                      {!positionData.is_sms_consent_required && (
                        <span className="text-[#858c78]">
                          {positionData.translations?.['apply.sms_consent_optional'] || '(Optional)'}
                        </span>
                      )}
                    </label>
                  </div>
                )}

                {infojobsApiKey && (
                  <div className="hidden">
                    <div
                      {...({ name: 'widget-holder' } as any)}
                      className="widget-holder"
                      data-api-key={infojobsApiKey}
                      data-rendering-size="1"
                      data-color="black"
                      data-callback-function="handleProfileData"
                    ></div>
                  </div>
                )}

                <button
                  type="button"
                  disabled={isSubmitting || isSubmitted || isRecaptchaProcessing}
                  className="w-full bg-[#3d4a2e] hover:bg-[#2e3822] text-white font-semibold py-3 rounded-lg transition disabled:opacity-60 disabled:cursor-not-allowed mt-1 custom-color g-recaptcha"
                  data-sitekey={recaptchaSiteKey}
                  data-callback="onSubmit"
                  data-action="apply_to_job"
                  onClick={() => {
                    if (isSubmitted) return;
                    setIsRecaptchaProcessing(true);
                    setTimeout(() => {
                      if (!isSubmittingRef.current && !isSubmittedRef.current) {
                        if (recaptchaSiteKey && (window as any).grecaptcha?.enterprise) {
                          (window as any).grecaptcha.enterprise.execute(recaptchaSiteKey, { action: 'apply_to_job' })
                            .then((token: string) => { (window as any).onSubmit(token); })
                            .catch(() => { (window as any).onSubmit(''); });
                        } else {
                          (window as any).onSubmit('');
                        }
                      }
                    }, 3000);
                  }}
                >
                  {isSubmitting ? (positionData.translations?.['apply.submitting'] || 'Sending...') : isRecaptchaProcessing ? (positionData.translations?.['apply.verifying'] || 'Verifying...') : positionData.translations?.['apply.submit']}
                </button>

                <div className="text-[11px] text-[#666d5a] text-center tracking-[0.04em]">
                  Powered by Orbio
                </div>

                {formError && (
                  <div role="alert" className="text-center text-red-500 font-semibold py-2 text-sm">
                    {formError}
                  </div>
                )}
              </form>
              ) : (
              <div className="bg-[#edefe8] border border-[#dde0d4] rounded-xl p-6 mb-8 md:mb-0 text-center">
                <div className="mx-auto w-14 h-14 bg-[#d4dbc8] rounded-full flex items-center justify-center mb-4">
                  <svg className="w-7 h-7 text-[#3d4a2e]" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="font-serif text-[22px] font-normal text-[#1e2418] tracking-[-0.01em] mb-2">
                  {isReferral
                    ? (positionData.translations?.['apply.referral_success_title'] || 'Referral sent!')
                    : positionData.translations?.['apply.success_title']}
                </h2>
                <p className="text-[#555c4a] text-sm leading-relaxed">
                  {isReferral
                    ? (positionData.translations?.['apply.referral_success_message'] || 'Thank you for your referral. We will review the candidate and keep you informed.')
                    : submittedThisSession
                      ? positionData.translations?.['apply.success_message']
                      : positionData.translations?.['apply.success_already_applied_message']
                  }
                </p>
                {isReferral && (
                  <button
                    type="button"
                    onClick={resetReferralForm}
                    className="mt-4 inline-flex items-center justify-center rounded-lg border border-[#dde0d4] bg-white px-4 py-2 text-sm font-medium text-[#1e2418] hover:bg-[#f5f6f1] transition focus:outline-none"
                  >
                    {positionData.translations?.['apply.referral_refer_another'] || 'Refer another candidate'}
                  </button>
                )}
              </div>
              )}
            </aside>
          </div>

          {/* Company Info */}
          <div className="mt-14">
            <section className="bg-[#edefe8] border border-[#dde0d4] rounded-xl p-6 md:p-8">
              <h3 className="font-serif text-[22px] font-normal text-[#1e2418] tracking-[-0.01em] mb-4">{positionData.company_name}</h3>
              <div className="prose max-w-none text-[#555c4a] text-[15px] leading-[1.7]">
                <ReactMarkdown
                  components={{
                    h1: ({ children }) => <h1 className="font-serif text-lg font-normal text-[#1e2418] mb-3">{children}</h1>,
                    h2: ({ children }) => <h2 className="font-serif text-base font-normal text-[#1e2418] mb-2">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-sm font-semibold text-[#1e2418] mb-2">{children}</h3>,
                    h4: ({ children }) => <h4 className="text-sm font-semibold text-[#1e2418] mb-2">{children}</h4>,
                    p: ({ children }) => <p className="mb-3 text-justify md:text-left">{children}</p>,
                    ul: ({ children }) => <ul className="list-disc list-inside mb-3">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal list-inside mb-3">{children}</ol>,
                    li: ({ children }) => <li className="mb-1">{children}</li>,
                    strong: ({ children }) => <strong className="font-semibold text-[#1e2418]">{children}</strong>,
                    em: ({ children }) => <em className="italic">{children}</em>,
                    a: ({ href, children }) => (
                      <a href={href} className="text-[#3d4a2e] underline hover:text-[#1e2418]" target="_blank" rel="noopener noreferrer">
                        {children}
                      </a>
                    ),
                  }}
                >
                  {positionData.company_description || ''}
                </ReactMarkdown>
              </div>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full text-center text-xs text-[#666d5a] py-6 mt-auto">
        &copy; {new Date().getFullYear()} Orbio
      </footer>

      <MarkdownModal
        open={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        content={positionData.privacy_policy || ''}
      />
      <MarkdownModal
        open={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        content={positionData.terms_of_service || ''}
      />
      <MarkdownModal
        open={showContactConsentModal}
        onClose={() => setShowContactConsentModal(false)}
        content={positionData.contact_consent_text || ''}
      />
      <MarkdownModal
        open={showSmsConsentModal}
        onClose={() => setShowSmsConsentModal(false)}
        content={positionData.sms_consent_text || ''}
      />
    </div>
  );
};

interface MarkdownModalProps {
  open: boolean;
  onClose: () => void;
  content: string;
}

const MarkdownModal: React.FC<MarkdownModalProps> = ({ open, onClose, content }) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-4xl w-full p-8 relative animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-4 text-[#666d5a] hover:text-[#1e2418] text-2xl font-bold"
        >
          &times;
        </button>
        <div className="text-[#555c4a] text-sm max-h-[70vh] overflow-y-auto prose">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ children }) => <h1 className="text-lg font-bold mb-3">{children}</h1>,
              h2: ({ children }) => <h2 className="text-base font-bold mb-2">{children}</h2>,
              h3: ({ children }) => <h3 className="text-sm font-bold mb-2">{children}</h3>,
              h4: ({ children }) => <h4 className="text-sm font-semibold mb-2">{children}</h4>,
              p: ({ children }) => <p className="mb-3">{children}</p>,
              ul: ({ children }) => <ul className="list-disc list-inside mb-3">{children}</ul>,
              ol: ({ children }) => <ol className="list-decimal list-inside mb-3">{children}</ol>,
              li: ({ children }) => <li className="mb-1">{children}</li>,
              strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
              em: ({ children }) => <em className="italic">{children}</em>,
              a: ({ href, children }) => (
                <a href={href} className="text-[#3d4a2e] underline hover:text-[#1e2418]" target="_blank" rel="noopener noreferrer">
                  {children}
                </a>
              ),
              table: ({ children }) => (
                <div className="overflow-x-auto mb-4">
                  <table className="min-w-full border-collapse border border-[#dde0d4] text-sm">{children}</table>
                </div>
              ),
              thead: ({ children }) => <thead className="bg-[#edefe8]">{children}</thead>,
              tbody: ({ children }) => <tbody>{children}</tbody>,
              tr: ({ children }) => <tr className="border-b border-[#dde0d4]">{children}</tr>,
              th: ({ children }) => <th className="border border-[#dde0d4] px-3 py-2 text-left font-semibold text-[#1e2418]">{children}</th>,
              td: ({ children }) => <td className="border border-[#dde0d4] px-3 py-2 align-top">{children}</td>,
            }}
          >
            {content}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
};

const PositionDetails: React.FC<PositionDetailsProps> = ({ isInternal = false, isReferral = false }) => {
  return <PositionDetailsContent isInternal={isInternal} isReferral={isReferral} />;
};

export default PositionDetails;
